import React, { Component } from 'react'
import { Col, Row, CardHeader, CardBody, Button, Table, ButtonGroup } from 'reactstrap'
import PropTypes from 'prop-types'
import { Input, InputGroup, InputGroupAddon } from 'reactstrap'
import { graphql, compose } from 'react-apollo'
import ConfirmModal from '../Alerts/ConfirmModal'
import ExportWpsModal from './export_wps/ExportWpsModal'
import ExportWpsBtn from './export_wps/ExportWpsBtn'
import { WpsListQuery, destroyWpsMutation } from './mutations'
import BlockWithLoader from '../Support/BlockWithLoader'
import { getUserPermissions } from '../../utils/userPermissions'

const PAGE_SIZE = 20

class WpsList extends Component {

  constructor(props) {
    super(props)
    this.state = {
      wpsList: [],
      page: 1,
      hasMore: true,
      loadingMore: false,
      wpsIdRemove: null,
      exportWpsModalOpened: false,
      noFilter: '',
      noFilterApplied: '',
    }
    this.tableContainerRef = React.createRef()
    this.closeConfirmModal = this.closeConfirmModal.bind(this)
    this.runRemoveWps = this.runRemoveWps.bind(this)
    this.handleScroll = this.handleScroll.bind(this)
    this.accumulatedList = []
    this.lastFetchedPage = 0
  }

  static propTypes = {
    data: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    destroyWpsMutation: PropTypes.func.isRequired,
  }

  componentDidMount() {
    const container = this.tableContainerRef.current
    if (container) container.addEventListener('scroll', this.handleScroll)
  }

  componentWillUnmount() {
    const container = this.tableContainerRef.current
    if (container) container.removeEventListener('scroll', this.handleScroll)
  }

  componentWillReceiveProps(nextProps) {
    const wasLoading = this.props.data.loading
    const isLoading = nextProps.data.loading
    const nextData = nextProps.data.wps_list

    // navigation to this page — reset and refetch
    if (!wasLoading && !isLoading &&
      (nextProps.history.action === 'PUSH' || nextProps.history.action === 'POP')) {
      this.lastFetchedPage = 0
      this.setState({ wpsList: [], page: 1, hasMore: true, loadingMore: false }, () => {
        this.props.data.refetch({ page: 1, pageSize: PAGE_SIZE, no: this.state.noFilterApplied || undefined })
      })
      return
    }

    // initial load or refetch (page 1 only)
    if (!isLoading && nextData && nextData.metadata.current_page === 1) {
      const incomingPage = nextData.metadata.current_page
      if (incomingPage === this.lastFetchedPage) return
      this.lastFetchedPage = incomingPage
      this.setState({
        wpsList: nextData.list.slice(0),
        hasMore: nextData.metadata.next_page !== null,
        loadingMore: false,
      })
    }
  }

  handleScroll() {
    const container = this.tableContainerRef.current
    if (!container || this.state.loadingMore || !this.state.hasMore) return
    const { scrollTop, scrollHeight, clientHeight } = container
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      this.loadMore()
    }
  }

  applyFilter = () => {
    const no = this.state.noFilter.trim() || undefined
    this.lastFetchedPage = 0
    this.setState({ wpsList: [], page: 1, hasMore: true, loadingMore: false, noFilterApplied: this.state.noFilter.trim() }, () => {
      this.props.data.refetch({ page: 1, pageSize: PAGE_SIZE, no })
    })
  }

  clearFilter = () => {
    this.lastFetchedPage = 0
    this.setState({ noFilter: '', noFilterApplied: '', wpsList: [], page: 1, hasMore: true, loadingMore: false }, () => {
      this.props.data.refetch({ page: 1, pageSize: PAGE_SIZE, no: undefined })
    })
  }

  loadMore() {
    const nextPage = this.state.page + 1
    this.setState({ loadingMore: true, page: nextPage })
    this.props.data.fetchMore({
      variables: { page: nextPage, pageSize: PAGE_SIZE, no: this.state.noFilterApplied || undefined },
      updateQuery: (prev, { fetchMoreResult }) => prev,
    }).then(({ data }) => {
      if (!data || !data.wps_list) return
      const incoming = data.wps_list.list
      const meta = data.wps_list.metadata
      this.lastFetchedPage = meta.current_page
      this.setState(prev => ({
        wpsList: [...prev.wpsList, ...incoming],
        hasMore: meta.next_page !== null,
        loadingMore: false,
      }))
    })
  }

  runRemoveWps() {
    this.removeWps(this.state.wpsIdRemove)
  }

  closeConfirmModal() {
    this.setState({ wpsIdRemove: null })
  }

  removeWps(id) {
    this.props.destroyWpsMutation({ variables: { id } }).then(() => {
      this.setState(prev => ({
        wpsList: prev.wpsList.filter(item => item.id !== id),
        wpsIdRemove: null,
      }))
    })
  }

  renderListRow(wps) {
    let wpsList = []
    let firstRow = null;

    (firstRow = wps.used_base_materials[0]) &&
      wps.used_base_materials.map(used_base_material => ((firstRow === used_base_material &&
        wpsList.push(
          <tr key={wps.id}>
            <td className="align-middle text-center" rowSpan={wps.used_base_materials.length}><b>{wps.id}</b></td>
            <td className="align-middle text-center" rowSpan={wps.used_base_materials.length}><b>{wps.rev_no}</b></td>
            <td className="align-middle text-center" rowSpan={wps.used_base_materials.length}>
              <b>{wps.welding_processes_names}</b>
            </td>
            <td className="align-middle text-center"><b>{used_base_material.product_type && used_base_material.product_type.name || ''}</b></td>
            <td className="align-middle text-center"><b>{used_base_material.base_material_designation_name}</b></td>
            <td className="align-middle text-center"><b>{used_base_material.thickness_range}</b></td>
            <td className="align-middle text-center"><b>{used_base_material.diameter_range}</b></td>
            <td className="align-middle text-center" rowSpan={wps.used_base_materials.length}>
              <b>{wps.joint_types_names}</b>
            </td>
            <td className="align-middle text-center" rowSpan={wps.used_base_materials.length}><b>{wps.no}</b></td>
            <td className="align-middle text-center" rowSpan={wps.used_base_materials.length}>
              <b>{wps.welding_positions_names}</b>
            </td>
            <td className="align-middle text-center" rowSpan={wps.used_base_materials.length}><b>{wps.standard.name}</b></td>
            <td className="align-middle text-center" rowSpan={wps.used_base_materials.length}>
              {wps.actual === false && <i className="fa fa-remove" />}
              {wps.actual === true && <i className="fa fa-check" />}
            </td>
            <td className="align-middle text-center" rowSpan={wps.used_base_materials.length}><b>{wps.release_date}</b></td>
            <td className="align-middle text-center" rowSpan={wps.used_base_materials.length}>
              <ButtonGroup size="sm">
                {getUserPermissions('wl_welds.wps.destroy') &&
                  <Button id="remove-wps-btn" color="danger" className="float-right" title="Remove" onClick={(e) => {
                    e.stopPropagation()
                    this.setState({ wpsIdRemove: wps.id })
                  }}>
                    <i className="fa fa-trash-o" />
                  </Button>}
                {getUserPermissions('wl_welds.wps.update') &&
                  <Button id="edit-wps-btn" color="warning" className="float-right" title="Edit" onClick={(e) => {
                    e.stopPropagation()
                    this.props.history.push(`/wps/${wps.id}/edit`)
                  }}>
                    <i className="fa fa-edit" />
                  </Button>}
                <ExportWpsBtn wps_id={Number(wps.id)} />
              </ButtonGroup>
            </td>
          </tr>)) ||

        (firstRow !== used_base_material && wpsList.push(<tr key={`u_${wps.id}_${used_base_material.id}`}>
          <td className="align-middle text-center"><b>{used_base_material.product_type && used_base_material.product_type.name || ''}</b></td>
          <td className="align-middle text-center"><b>{used_base_material.base_material_designation_name}</b></td>
          <td className="align-middle text-center"><b>{used_base_material.thickness_range}</b></td>
          <td className="align-middle text-center"><b>{used_base_material.diameter_range}</b></td>
        </tr>))
      ))

    return wpsList
  }

  renderList() {
    return (
      <React.Fragment>
        {this.state.wpsList.map(wps =>
          <tbody key={wps.id} onClick={() => this.props.history.push(`/wps/${wps.id}/view`)}>{this.renderListRow(wps)}</tbody>
        )}
        {this.state.loadingMore && (
          <tbody>
            <tr>
              <td colSpan={14} className="text-center py-2">
                <i className="fa fa-spinner fa-spin" /> Loading...
              </td>
            </tr>
          </tbody>
        )}
      </React.Fragment>
    )
  }

  render() {
    if (this.props.data.error) {
      return (<div>An unexpected error occurred</div>)
    }

    return (
      <BlockWithLoader loading={this.props.data.loading} renderChildren={true}>
        {this.state.exportWpsModalOpened &&
          <ExportWpsModal onCloseFunction={() => this.setState({ exportWpsModalOpened: false })} />}
        <ConfirmModal
          isOpened={!!this.state.wpsIdRemove}
          onCancel={this.closeConfirmModal}
          onConfirm={this.runRemoveWps}
          headerText='Remove WPS'
          bodyText='Are you sure you want to delete this WPS?'
          confirmBtnText='Remove'
        />
        <CardHeader>
          <b>WPS</b>
          {getUserPermissions('wl_welds.wps.create') &&
            <Button id="add-new-wps-btn" color="primary"
              className="float-right btn-add-project btn-element"
              onClick={() => this.props.history.push('/wps/new')}
            >
              <i className="fa fa-plus" /> Add new WPS
            </Button>}
          {getUserPermissions('wl_welds.export_wps_list.export_wps_list') &&
            <Button id="export-welders-btn" color="primary" title="Export"
              className="float-right btn-element"
              onClick={(e) => { e.stopPropagation(); this.setState({ exportWpsModalOpened: true }) }}
            >
              <i className="fa fa-print" /> Export
            </Button>}
          {getUserPermissions('wl_welds.wps.assign_projects') &&
            <Button id="assign-projects-btn" color="secondary" title="Assign WPS to projects"
              className="float-right btn-element"
              onClick={() => this.props.history.push('/wps/assign-projects')}
            >
              <i className="fa fa-folder-open" /> Assign to projects
            </Button>}
        </CardHeader>

        <CardBody>
          <Row>
            <Col md="12">
              <div style={{ marginBottom: 8 }}>
                <InputGroup>
                  <Input
                    placeholder="Filter by WPS No..."
                    value={this.state.noFilter}
                    onChange={e => this.setState({ noFilter: e.target.value })}
                    onKeyDown={e => e.key === 'Enter' && this.applyFilter()}
                    disabled={this.props.data.loading}
                  />
                  <InputGroupAddon addonType="append">
                    <Button color="primary" onClick={this.applyFilter} disabled={this.props.data.loading}>
                      <i className="fa fa-filter" /> Filter
                    </Button>
                    <Button color="secondary" onClick={this.clearFilter} disabled={this.props.data.loading}>
                      <i className="fa fa-times" /> Clear
                    </Button>
                  </InputGroupAddon>
                </InputGroup>
              </div>
              <div
                ref={this.tableContainerRef}
                style={{ maxHeight: '75vh', overflowY: 'auto', position: 'relative' }}
              >
                <Table bordered className="table-project table-tbody-hover" style={{ marginBottom: 0 }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#fff' }}>
                    <tr>
                      <th rowSpan={2} className="align-middle text-center" style={{ width: '60px' }}>ID</th>
                      <th rowSpan={2} className="align-middle text-center">Rev</th>
                      <th rowSpan={2} className="align-middle text-center">Welding processes</th>
                      <th colSpan={4} className="text-center">Base materials</th>
                      <th rowSpan={2} className="align-middle text-center">Joint type</th>
                      <th rowSpan={2} className="align-middle text-center">WPS No</th>
                      <th rowSpan={2} className="align-middle text-center">Welding position</th>
                      <th rowSpan={2} className="align-middle text-center">According to standard</th>
                      <th rowSpan={2} className="align-middle text-center">actual</th>
                      <th rowSpan={2} className="align-middle text-center">release_date</th>
                      <th rowSpan={2} className="align-middle text-center" style={{ width: '110px' }}>Actions</th>
                    </tr>
                    <tr>
                      <th className="text-center">Product type</th>
                      <th className="text-center">Base material grade</th>
                      <th className="text-center">Thickness range</th>
                      <th className="text-center">Diameter range</th>
                    </tr>
                  </thead>
                  {this.renderList()}
                </Table>
              </div>
            </Col>
          </Row>
        </CardBody>
      </BlockWithLoader>
    )
  }
}

const WpsListWithMutations = compose(
  graphql(WpsListQuery, {
    options: () => ({
      fetchPolicy: 'network-only',
      variables: { page: 1, pageSize: PAGE_SIZE },
    }),
  }),
  graphql(destroyWpsMutation, { name: 'destroyWpsMutation' })
)(WpsList)

export default WpsListWithMutations



// WEBPACK FOOTER //
// ./src/components/Wps/WpsList.js